import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Database, Server, CheckCircle, XCircle, RefreshCw, Activity, TrendingUp, AlertTriangle, Zap, Calendar, Key, Copy, Shield, Pause, Play } from 'lucide-react';
import { apiCallJson, apiCall } from './Api';
import { PageLayout, PageHeader, PageBody } from './PageLayout';

const HostManagement = ({ user }) => {
  const [hosts, setHosts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingHost, setEditingHost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);
  const [etlStatus, setEtlStatus] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [jobStatuses, setJobStatuses] = useState({});
  const [dateRange, setDateRange] = useState(() => {
  const saved = localStorage.getItem('etl_date_range');
    return saved ? JSON.parse(saved) : { 
      preset: 'last_7_days',
      start_date: '',
      end_date: ''
    };
  });

  const [hostForm, setHostForm] = useState({
    host_tenant_id: '',
    host_name: '',
    description: '',
    host_url: '',
    postgres_uri: 'postgresql://postgres:postgres@localhost:5432/globalprocon',
    mongo_uri: 'mongodb://admin:admin@localhost:27017/globalprocon',
    mongo_collection: 'MessageTx',
    table_name: 't_message',
    ssl_mode: 'prefer',
    connection_timeout: 30,
    sync_interval: 300,
    is_enable_etl: true
  });

  // Application management state
  const [selectedHostForApps, setSelectedHostForApps] = useState(null);
  const [applications, setApplications] = useState([]);
  const [showAppModal, setShowAppModal] = useState(false);
  const [appForm, setAppForm] = useState({
    name: '',
    domain: '',
    allowed_origins: '',
    default_permissions: ''
  });
  const [showAppSecret, setShowAppSecret] = useState({});
  const [loadingApps, setLoadingApps] = useState(false);
  const [newlyCreatedSecret, setNewlyCreatedSecret] = useState(null); // { app_id, app_secret, name }

  const loadHosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCallJson('/api/postgresql-hosts');
      setHosts(response.hosts || []);
    } catch (error) {
      console.error('Failed to load hosts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQueueStatus = useCallback(async () => {
    try {
      const data = await apiCallJson('/api/etl/queue-status');
      setQueueStatus(data);
    } catch (error) {
      console.error('Failed to load queue status:', error);
    }
  }, []);

  const loadETLStatus = useCallback(async (hostTenantId) => {
    try {
      const params = new URLSearchParams();
      if (dateRange.preset === 'custom' && dateRange.start_date && dateRange.end_date) {
        params.append('start_date', dateRange.start_date);
        params.append('end_date', dateRange.end_date);
      } else {
        params.append('time_range', dateRange.preset);
      }
      
      const data = await apiCallJson(`/api/postgresql-hosts/${hostTenantId}/etl-status?${params}`);
      setEtlStatus(data);
    } catch (error) {
      console.error('Failed to load ETL status:', error);
    }
  }, [dateRange]);

  const checkJobStatus = useCallback(async (jobId) => {
    try {
      const data = await apiCallJson(`/api/etl/job/${jobId}`);
      if (data.success && data.job) {
        setJobStatuses(prev => ({ ...prev, [jobId]: data.job }));
      }
    } catch (error) {
      console.error('Failed to check job status:', error);
    }
  }, []);

  useEffect(() => {
    loadHosts();
    loadQueueStatus();
    
    const interval = setInterval(() => {
      loadQueueStatus();
      if (selectedHost) {
        loadETLStatus(selectedHost.host_tenant_id);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [loadHosts, loadQueueStatus, loadETLStatus, selectedHost]);

  useEffect(() => {
    localStorage.setItem('etl_date_range', JSON.stringify(dateRange));
  }, [dateRange]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      if (editingHost) {
        await apiCall(`/api/postgresql-hosts/${editingHost.host_tenant_id}`, {
          method: 'PUT', 
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(hostForm)
        });
      } else {
        const response = await apiCallJson(`/api/postgresql-hosts`, {
          method: 'POST', 
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(hostForm)
        });
        
        if (response.job_id) {
          setTimeout(() => checkJobStatus(response.job_id), 2000);
        }
      }
      
      setShowModal(false);
      setEditingHost(null);
      loadHosts();
      resetForm();
    } catch (error) {
      alert(error.message || 'Failed to save host');
    }
  }, [editingHost, hostForm, loadHosts, checkJobStatus]);

  const testConnection = useCallback(async () => {
    try {
      setTestingConnection(true);
      const result = await apiCallJson('/api/postgresql-hosts/test-connection', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          postgres_uri: hostForm.postgres_uri,
          ssl_mode: hostForm.ssl_mode,
          table_name: hostForm.table_name,
          connection_timeout: hostForm.connection_timeout
        })
      });
      
      alert(result.message);
    } catch (error) {
      alert(error.message || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  }, [hostForm]);

  const triggerSync = useCallback(async (hostTenantId) => {
    try {
      const response = await apiCallJson(`/api/postgresql-hosts/${hostTenantId}/sync`, {
        method: 'POST'
      });
      
      if (response.job_id) {
        setTimeout(() => checkJobStatus(response.job_id), 2000);
      }
      
      alert('Sync triggered successfully');
      loadHosts();
    } catch (error) {
      alert(error.message || 'Failed to trigger sync');
    }
  }, [loadHosts, checkJobStatus]);

  const clearHostData = useCallback(async (hostTenantId, hostName) => {
    if (!window.confirm(`Are you sure you want to clear all data for "${hostName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await apiCall(`/api/postgresql-hosts/${hostTenantId}/clear`, {
        method: 'POST'
      });
      alert('Data clear initiated');
      loadHosts();
    } catch (error) {
      alert(error.message || 'Failed to clear data');
    }
  }, [loadHosts]);

  const triggerReferenceSync = useCallback(async (hostTenantId) => {
    try {
      const response = await apiCallJson(`/api/postgresql-hosts/${hostTenantId}/sync-reference`, {
        method: 'POST'
      });
      if (response.job_id) {
        setTimeout(() => checkJobStatus(response.job_id), 2000);
      }
      alert('Reference data sync queued (customers, vendors, services)');
      loadHosts();
    } catch (error) {
      alert(error.message || 'Failed to trigger reference sync');
    }
  }, [loadHosts, checkJobStatus]);

  const pauseEtl = useCallback(async (hostTenantId, hostName) => {
    if (!window.confirm(`Pause ETL for "${hostName}"? CDR sync will stop until resumed. Reference data will continue to sync on schedule.`)) {
      return;
    }
    try {
      await apiCall(`/api/postgresql-hosts/${hostTenantId}/pause-etl`, {
        method: 'POST'
      });
      alert('ETL paused');
      loadHosts();
    } catch (error) {
      alert(error.message || 'Failed to pause ETL');
    }
  }, [loadHosts]);

  const resumeEtl = useCallback(async (hostTenantId) => {
    try {
      await apiCall(`/api/postgresql-hosts/${hostTenantId}/resume-etl`, {
        method: 'POST'
      });
      alert('ETL resumed');
      loadHosts();
    } catch (error) {
      alert(error.message || 'Failed to resume ETL');
    }
  }, [loadHosts]);

  // Per-host connectivity check results, keyed by host_tenant_id.
  // Shape: { [host_tenant_id]: { loading, pg, mongo, all_ok, ts } }
  const [connectivity, setConnectivity] = useState({});

  const checkConnectivity = useCallback(async (hostTenantId) => {
    setConnectivity(prev => ({
      ...prev,
      [hostTenantId]: { ...(prev[hostTenantId] || {}), loading: true },
    }));
    try {
      const data = await apiCallJson(
        `/api/postgresql-hosts/${hostTenantId}/test-connectivity`,
        { method: 'GET' }
      );
      setConnectivity(prev => ({
        ...prev,
        [hostTenantId]: {
          loading: false,
          pg: data.postgresql,
          mongo: data.mongodb,
          all_ok: data.all_ok,
          ts: new Date().toISOString(),
        },
      }));
    } catch (error) {
      setConnectivity(prev => ({
        ...prev,
        [hostTenantId]: {
          loading: false,
          pg: { success: false, message: error.message || 'Request failed' },
          mongo: { success: false, message: 'Not tested' },
          all_ok: false,
          ts: new Date().toISOString(),
        },
      }));
    }
  }, []);

  const deleteHost = useCallback(async (host) => {
    if (!window.confirm(`Are you sure you want to delete host "${host.host_name}"? This will delete all associated data.`)) {
      return;
    }
    
    try {
      await apiCall(`/api/postgresql-hosts/${host.host_tenant_id}`, {
        method: 'DELETE'
      });
      alert('Host deletion initiated');
      loadHosts();
    } catch (error) {
      alert(error.message || 'Failed to delete host');
    }
  }, [loadHosts]);

  const viewETLStatus = useCallback((host) => {
    setSelectedHost(host);
    loadETLStatus(host.host_tenant_id);
  }, [loadETLStatus]);

  const openEditModal = useCallback((host) => {
    setEditingHost(host);
    setHostForm({
      host_tenant_id: host.host_tenant_id,
      host_name: host.host_name,
      description: host.description || '',
      host_url: host.host_url || '',
      postgres_uri: host.postgres_uri || '',
      mongo_uri: host.mongo_uri || '',
      mongo_collection: host.mongo_collection || 'MessageTx',
      table_name: host.table_name || 't_message',
      ssl_mode: host.ssl_mode || 'prefer',
      connection_timeout: host.connection_timeout || 30,
      sync_interval: host.sync_interval || 300,
      is_enable_etl: host.is_enable_etl !== false
    });
    setShowModal(true);
  }, []);

  // Soft-help: when postgres_uri host changes, mirror it to mongo_uri
  // only if the mongo_uri's current host matches the previous postgres_uri host
  // (i.e. user hadn't intentionally set a different Mongo host).
  const handlePostgresUriChange = useCallback((newPgUri) => {
    setHostForm((prev) => {
      const next = { ...prev, postgres_uri: newPgUri };

      const pgHostRe = /^postgres(?:ql)?:\/\/(?:[^/@]*@)?([^:/?#]+)/i;
      const newHost = newPgUri.match(pgHostRe)?.[1];
      const oldHost = prev.postgres_uri.match(pgHostRe)?.[1];

      if (newHost && newHost !== oldHost && prev.mongo_uri) {
        const mongoRe = /^(mongodb(?:\+srv)?:\/\/(?:[^/@]*@)?)([^:/?#]+)(:\d+)?(\/.*)?$/i;
        const m = prev.mongo_uri.match(mongoRe);
        if (m && m[2] === oldHost) {
          next.mongo_uri = `${m[1]}${newHost}${m[3] || ''}${m[4] || ''}`;
        }
      }

      return next;
    });
  }, []);

  const resetForm = () => {
    setHostForm({
      host_tenant_id: '',
      host_name: '',
      description: '',
      host_url: '',
      postgres_uri: 'postgresql://postgres:postgres@localhost:5432/globalprocon',
      mongo_uri: 'mongodb://admin:admin@localhost:27017/globalprocon',
      mongo_collection: 'MessageTx',
      table_name: 't_message',
      ssl_mode: 'prefer',
      connection_timeout: 30,
      sync_interval: 300,
      is_enable_etl: true
    });
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-gray-100 text-gray-800', icon: AlertTriangle },
      running: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        <span>{status}</span>
      </span>
    );
  };

  const DateRangeSelector = () => {
    const presets = [
      { value: 'last_7_days', label: 'Last 7 Days' },
      { value: 'last_30_days', label: 'Last 30 Days' },
      { value: 'last_90_days', label: 'Last 90 Days' },
      { value: 'custom', label: 'Custom Range' }
    ];

    return (
      <div className="flex items-center gap-3 mb-4">
        <Calendar className="w-5 h-5 text-gray-600" />
        <select
          value={dateRange.preset}
          onChange={(e) => setDateRange({ ...dateRange, preset: e.target.value })}
          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {presets.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        
        {dateRange.preset === 'custom' && (
          <>
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </>
        )}
        
        <button
          onClick={() => selectedHost && loadETLStatus(selectedHost.host_tenant_id)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>
    );
  };

  const getLatestSyncTime = (lastSyncTime) => {
    if (!lastSyncTime) return null;
    
    try {
      // If it's a JSON string, parse it
      const syncTimes = typeof lastSyncTime === 'string' ? JSON.parse(lastSyncTime) : lastSyncTime;
      
      // If it's an object with table-wise sync times, get the latest one
      if (typeof syncTimes === 'object' && syncTimes !== null) {
        const times = Object.values(syncTimes).filter(t => t);
        if (times.length === 0) return null;
        
        // Find the most recent sync time
        const latest = times.reduce((max, current) => {
          const maxDate = new Date(max);
          const currentDate = new Date(current);
          return currentDate > maxDate ? current : max;
        });
        
        return latest;
      }
      
      // If it's a direct date string
      return syncTimes;
    } catch (e) {
      // If parsing fails, return as-is
      return lastSyncTime;
    }
  };

  const getNextSyncTime = (lastSyncTime, syncInterval = 300) => {
    if (!lastSyncTime) return null;
    
    try {
      // Get the latest sync time from all tables
      const latestSync = getLatestSyncTime(lastSyncTime);
      if (!latestSync) return null;
      
      // Calculate next sync time by adding sync interval
      const lastSyncDate = new Date(latestSync);
      const nextSyncDate = new Date(lastSyncDate.getTime() + (syncInterval * 1000));
      
      return nextSyncDate.toISOString();
    } catch (e) {
      return null;
    }
  };

  const isAdmin = user?.role === 'admin';
  const isHostAdmin = user?.role === 'host_admin';

  const loadApplications = useCallback(async (host) => {
    setSelectedHostForApps(host);
    setLoadingApps(true);
    try {
      const data = await apiCallJson(`/api/auth/applications?host_tenant_id=${host.host_tenant_id}`);
      setApplications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load applications:', error);
      setApplications([]);
    } finally {
      setLoadingApps(false);
    }
  }, []);

  const handleCreateApp = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedHostForApps) return;
    
    try {
      const payload = {
        name: appForm.name,
        domain: appForm.domain,
        host_tenant_id: selectedHostForApps.host_tenant_id,
        allowed_origins: appForm.allowed_origins.split(',').map(s => s.trim()).filter(Boolean),
        default_permissions: appForm.default_permissions.split(',').map(s => s.trim()).filter(Boolean),
        is_active: true
      };
      
      const result = await apiCallJson('/api/auth/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      setShowAppModal(false);
      setAppForm({ name: '', domain: '', allowed_origins: '', default_permissions: '' });
      
      if (result.app_secret) {
        setNewlyCreatedSecret({
          app_id: result.app_id,
          app_secret: result.app_secret,
          name: appForm.name
        });
      }
      
      loadApplications(selectedHostForApps);
    } catch (error) {
      alert(error.message || 'Failed to create application');
    }
  }, [selectedHostForApps, appForm, loadApplications]);

  const handleDeleteApp = useCallback(async (appId, appName) => {
    if (!window.confirm(`Delete application "${appName}"? This will invalidate all sessions using this app.`)) return;
    
    try {
      await apiCall(`/api/auth/applications/${appId}`, { method: 'DELETE' });
      loadApplications(selectedHostForApps);
    } catch (error) {
      alert(error.message || 'Failed to delete application');
    }
  }, [selectedHostForApps, loadApplications]);

  const handleRegenerateSecret = useCallback(async (appId, appName) => {
    if (!window.confirm(`Regenerate secret for "${appName}"? All existing sessions will be invalidated.`)) return;
    
    try {
      const result = await apiCallJson(`/api/auth/applications/${appId}/regenerate-secret`, { method: 'POST' });
      if (result.app_secret) {
        setNewlyCreatedSecret({
          app_id: appId,
          app_secret: result.app_secret,
          name: appName
        });
      }
      loadApplications(selectedHostForApps);
    } catch (error) {
      alert(error.message || 'Failed to regenerate secret');
    }
  }, [selectedHostForApps, loadApplications]);

  const [copyFeedback, setCopyFeedback] = useState(null);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback(label || 'Copied!');
      setTimeout(() => setCopyFeedback(null), 2000);
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopyFeedback(label || 'Copied!');
      setTimeout(() => setCopyFeedback(null), 2000);
    });
  };

  return (
    <PageLayout>

      {/* Application Management Modal */}
      {selectedHostForApps && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Applications: {selectedHostForApps.host_name}</h2>
                <p className="text-sm text-gray-500">Tenant: {selectedHostForApps.host_tenant_id}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setAppForm({ name: '', domain: '', allowed_origins: '', default_permissions: '' });
                    setShowAppModal(true);
                  }}
                  className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Register App</span>
                </button>
                <button
                  onClick={() => { setSelectedHostForApps(null); setNewlyCreatedSecret(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Copy feedback toast */}
              {copyFeedback && (
                <div className="fixed top-4 right-4 z-[70] bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg text-sm animate-pulse">
                  ✓ {copyFeedback}
                </div>
              )}

              {/* Newly created/regenerated secret banner */}
              {newlyCreatedSecret && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Shield className="w-5 h-5 text-amber-600" />
                        <h4 className="font-semibold text-amber-900">
                          Credentials for "{newlyCreatedSecret.name}"
                        </h4>
                      </div>
                      <p className="text-xs text-amber-700 mb-3">
                        ⚠️ Copy and save the secret now — it cannot be retrieved later.
                      </p>
                      
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-gray-600 w-16">App ID</span>
                          <code className="flex-1 bg-white border border-amber-200 px-3 py-1.5 rounded text-sm font-mono select-all">
                            {newlyCreatedSecret.app_id}
                          </code>
                          <button
                            onClick={() => copyToClipboard(newlyCreatedSecret.app_id, 'App ID copied!')}
                            className="px-2 py-1.5 text-amber-700 bg-amber-100 rounded hover:bg-amber-200"
                            title="Copy App ID"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-gray-600 w-16">Secret</span>
                          <code className="flex-1 bg-white border border-amber-200 px-3 py-1.5 rounded text-sm font-mono select-all">
                            {newlyCreatedSecret.app_secret}
                          </code>
                          <button
                            onClick={() => copyToClipboard(newlyCreatedSecret.app_secret, 'Secret copied!')}
                            className="px-2 py-1.5 text-amber-700 bg-amber-100 rounded hover:bg-amber-200"
                            title="Copy Secret"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setNewlyCreatedSecret(null)}
                      className="ml-3 text-amber-400 hover:text-amber-600"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {loadingApps ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">Loading applications...</p>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Shield className="w-10 h-10 text-gray-400 mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">No applications registered for this host</p>
                  <p className="text-xs text-gray-400 mt-1">Register an application to enable API access</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.map(app => (
                    <div key={app.app_id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-gray-900">{app.name}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              app.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {app.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">Domain: {app.domain}</p>
                          
                          <div className="mt-2 space-y-1 text-xs text-gray-600">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">App ID:</span>
                              <code className="bg-gray-100 px-2 py-0.5 rounded">{app.app_id}</code>
                              <button onClick={() => copyToClipboard(app.app_id, 'App ID copied!')} className="text-blue-600 hover:text-blue-800">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                            {app.allowed_origins && app.allowed_origins.length > 0 && (
                              <div>
                                <span className="font-medium">Origins:</span> {Array.isArray(app.allowed_origins) ? app.allowed_origins.join(', ') : app.allowed_origins}
                              </div>
                            )}
                            {app.default_permissions && app.default_permissions.length > 0 && (
                              <div>
                                <span className="font-medium">Permissions:</span> {Array.isArray(app.default_permissions) ? app.default_permissions.join(', ') : app.default_permissions}
                              </div>
                            )}
                            {app.created_at && (
                              <div>
                                <span className="font-medium">Created:</span> {formatDate(app.created_at)}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleRegenerateSecret(app.app_id, app.name)}
                            className="flex items-center space-x-1 px-2 py-1 text-xs text-orange-700 bg-orange-50 rounded hover:bg-orange-100"
                            title="Regenerate Secret"
                          >
                            <RefreshCw className="w-3 h-3" />
                            <span>Regen Secret</span>
                          </button>
                          <button
                            onClick={() => handleDeleteApp(app.app_id, app.name)}
                            className="flex items-center space-x-1 px-2 py-1 text-xs text-red-700 bg-red-50 rounded hover:bg-red-100"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Register Application Modal */}
      {showAppModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="border-b p-4">
              <h3 className="text-lg font-bold text-gray-900">Register New Application</h3>
              <p className="text-sm text-gray-500">For tenant: {selectedHostForApps?.host_tenant_id}</p>
            </div>
            
            <form onSubmit={handleCreateApp} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Name *</label>
                <input
                  type="text"
                  required
                  value={appForm.name}
                  onChange={(e) => setAppForm({...appForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="My API Client"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domain *</label>
                <input
                  type="text"
                  required
                  value={appForm.domain}
                  onChange={(e) => setAppForm({...appForm, domain: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="api.example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Origins</label>
                <input
                  type="text"
                  value={appForm.allowed_origins}
                  onChange={(e) => setAppForm({...appForm, allowed_origins: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://app1.com, https://app2.com"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated list of allowed CORS origins</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Permissions</label>
                <input
                  type="text"
                  value={appForm.default_permissions}
                  onChange={(e) => setAppForm({...appForm, default_permissions: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="olap:read, dashboard:read"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated list of permissions (e.g. olap:read, dashboard:read)</p>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowAppModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Register Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Header */}
      <PageHeader
        title="Customer Host Management"
        icon={Server}
        actions={
          (isAdmin) && (
            <button
              onClick={() => {
                setEditingHost(null);
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Host</span>
            </button>
          )
        }
      />

      <PageBody>
      {/* Queue Status Banner */}
      {queueStatus && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Activity className="w-5 h-5 text-blue-600" />
              <div>
                <div className="text-sm font-medium text-gray-900">ETL Queue Status</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {queueStatus.pending_jobs} pending • {queueStatus.active_jobs} active
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-900">{queueStatus.total_jobs}</div>
              <div className="text-xs text-gray-600">Total Jobs</div>
            </div>
          </div>
        </div>
      )}

      {/* Hosts List */}
      <div className="grid grid-cols-1 gap-6">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Loading hosts...</p>
          </div>
        ) : hosts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Database className="w-12 h-12 text-gray-400 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">No hosts configured</p>
          </div>
        ) : (
          hosts.map(host => (
            <div key={host.host_tenant_id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6">
                {/* Host Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <Server className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{host.host_name}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{host.description}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-xs text-gray-500">ID: {host.host_tenant_id}</span>
                        {getStatusBadge(host.sync_status)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      host.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {host.is_active ? 'Active' : 'Inactive'}
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      host.is_enable_etl !== false ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      ETL {host.is_enable_etl !== false ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                </div>

                {/* Connectivity result chips (only after Test Connections clicked) */}
                {connectivity[host.host_tenant_id] && !connectivity[host.host_tenant_id].loading && (
                  <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                    <span className="text-gray-500">Last tested:</span>
                    <span className="text-gray-400">
                      {new Date(connectivity[host.host_tenant_id].ts).toLocaleTimeString()}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded font-medium ${
                        connectivity[host.host_tenant_id].pg?.success
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                      title={connectivity[host.host_tenant_id].pg?.message || ''}
                    >
                      PG {connectivity[host.host_tenant_id].pg?.success ? '✓' : '✗'}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded font-medium ${
                        connectivity[host.host_tenant_id].mongo?.success
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                      title={connectivity[host.host_tenant_id].mongo?.message || ''}
                    >
                      Mongo {connectivity[host.host_tenant_id].mongo?.success ? '✓' : '✗'}
                    </span>
                    {!connectivity[host.host_tenant_id].all_ok && (
                      <span className="text-red-600 italic">
                        Hover the chip for the failure detail
                      </span>
                    )}
                  </div>
                )}

                {/* Connection Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">SMSC URL:</span>
                    <span className="ml-2 font-medium text-gray-900 text-xs break-all" title={host.host_url}>
                      {host.host_url || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">PostgreSQL:</span>
                    <span className="ml-2 font-medium text-gray-900 font-mono text-xs break-all" title={host.postgres_uri}>
                      {host.postgres_uri || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">MongoDB:</span>
                    <span className="ml-2 font-medium text-gray-900 font-mono text-xs break-all" title={host.mongo_uri}>
                      {host.mongo_uri || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Sync Interval:</span>
                    <span className="ml-2 font-medium text-gray-900">{host.sync_interval}s</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Sync:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {(() => {
                        const latestSync = getLatestSyncTime(host.last_sync_time);
                        return latestSync ? formatDate(latestSync) : 'Never';
                      })()}
                    </span>
                  </div>
                </div>

                {/* Error Message */}
                {host.error_message && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                    <strong>Error:</strong> {host.error_message}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  <button
                    onClick={() => triggerSync(host.host_tenant_id)}
                    disabled={host.sync_status === 'running'}
                    className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Zap className="w-3 h-3" />
                    <span>Sync Now</span>
                  </button>

                  <button
                    onClick={() => triggerReferenceSync(host.host_tenant_id)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 rounded hover:bg-teal-100"
                    title="Force-sync customers, vendors, services. Skips CDR — useful when reference data is missing."
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>Sync Reference</span>
                  </button>

                  <button
                    onClick={() => checkConnectivity(host.host_tenant_id)}
                    disabled={connectivity[host.host_tenant_id]?.loading}
                    className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 rounded hover:bg-cyan-100 disabled:opacity-50"
                    title="Test live connectivity to PostgreSQL and MongoDB"
                  >
                    <Activity className="w-3 h-3" />
                    <span>{connectivity[host.host_tenant_id]?.loading ? 'Testing…' : 'Test Connections'}</span>
                  </button>

                  {(isAdmin || isHostAdmin) && (
                    host.is_enable_etl !== false ? (
                      <button
                        onClick={() => pauseEtl(host.host_tenant_id, host.host_name)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded hover:bg-amber-100"
                        title="Pause CDR + Mongo ETL. Reference data continues to sync."
                      >
                        <Pause className="w-3 h-3" />
                        <span>Pause ETL</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => resumeEtl(host.host_tenant_id)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
                        title="Resume CDR + Mongo ETL."
                      >
                        <Play className="w-3 h-3" />
                        <span>Resume ETL</span>
                      </button>
                    )
                  )}

                  <button
                    onClick={() => viewETLStatus(host)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded hover:bg-purple-100"
                  >
                    <TrendingUp className="w-3 h-3" />
                    <span>Metrics</span>
                  </button>

                  {(isAdmin || isHostAdmin) && (
                    <>
                      <button
                        onClick={() => openEditModal(host)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                      >
                        <Edit className="w-3 h-3" />
                        <span>Edit</span>
                      </button>

                      <button
                        onClick={() => loadApplications(host)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100"
                      >
                        <Key className="w-3 h-3" />
                        <span>Applications</span>
                      </button>

                      {isAdmin && (
                        <>
                          <button
                            onClick={() => clearHostData(host.host_tenant_id, host.host_name)}
                            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 rounded hover:bg-orange-100"
                          >
                            <XCircle className="w-3 h-3" />
                            <span>Clear Data</span>
                          </button>

                          <button
                            onClick={() => deleteHost(host)}
                            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                          >
                            <Trash2 className="w-3 h-3" />
                            <span>Delete</span>
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ETL Status Modal */}
      {selectedHost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">ETL Status: {selectedHost.host_name}</h2>
              <button
                onClick={() => setSelectedHost(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <DateRangeSelector />

              {/* Table-wise Stats */}
              {etlStatus && etlStatus.table_stats && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Records by Table</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-blue-600 font-medium">Customers</div>
                      <div className="text-2xl font-bold text-blue-900 mt-1">
                        {formatNumber(etlStatus.table_stats.customers || 0)}
                      </div>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-green-600 font-medium">Customer Services</div>
                      <div className="text-2xl font-bold text-green-900 mt-1">
                        {formatNumber(etlStatus.table_stats.customer_services || 0)}
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-purple-600 font-medium">Vendors</div>
                      <div className="text-2xl font-bold text-purple-900 mt-1">
                        {formatNumber(etlStatus.table_stats.vendors || 0)}
                      </div>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-sm text-orange-600 font-medium">Vendor Services</div>
                      <div className="text-2xl font-bold text-orange-900 mt-1">
                        {formatNumber(etlStatus.table_stats.vendor_services || 0)}
                      </div>
                    </div>

                    <div className="bg-indigo-50 rounded-lg p-4">
                      <div className="text-sm text-indigo-600 font-medium">Country Networks</div>
                      <div className="text-2xl font-bold text-indigo-900 mt-1">
                        {formatNumber(etlStatus.table_stats.country_networks || 0)}
                      </div>
                    </div>
                    
                    <div className="bg-pink-50 rounded-lg p-4">
                      <div className="text-sm text-pink-600 font-medium">CDR</div>
                      <div className="text-2xl font-bold text-pink-900 mt-1">
                        {formatNumber(etlStatus.table_stats.cdr || 0)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sync Performance Stats */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Sync Performance</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm text-blue-600 font-medium">Total Syncs</div>
                    <div className="text-2xl font-bold text-blue-900 mt-1">
                      {formatNumber(etlStatus?.stats?.total_syncs || 0)}
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm text-green-600 font-medium">Success Rate</div>
                    <div className="text-2xl font-bold text-green-900 mt-1">
                      {etlStatus?.stats?.success_rate || 0}%
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-sm text-purple-600 font-medium">Records Synced</div>
                    <div className="text-2xl font-bold text-purple-900 mt-1">
                      {formatNumber(etlStatus?.stats?.total_records_synced || 0)}
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="text-sm text-orange-600 font-medium">Avg Duration</div>
                    <div className="text-2xl font-bold text-orange-900 mt-1">
                      {etlStatus?.stats?.avg_duration_seconds || 0}s
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Status */}
              {etlStatus?.host_status && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Current Status</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Health:</span>
                      <span className={`ml-2 font-medium ${
                        etlStatus.host_status.health_status === 'healthy' ? 'text-green-600' :
                        etlStatus.host_status.health_status === 'stale' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {etlStatus.host_status.health_status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Last Sync:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(() => {
                          const latestSync = getLatestSyncTime(etlStatus.host_status.last_sync_time);
                          return latestSync ? formatDate(latestSync) : 'Never';
                        })()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Next Sync:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(() => {
                          const nextSync = getNextSyncTime(
                            etlStatus.host_status.last_sync_time,
                            etlStatus.host_status.sync_interval || selectedHost?.sync_interval || 300
                          );
                          return nextSync ? formatDate(nextSync) : 'N/A';
                        })()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Sync Status:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {etlStatus.host_status.sync_status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Host Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingHost ? 'Edit Host' : 'Add New Host'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Host Tenant ID * {editingHost && '(Read-only)'}
                    </label>
                    <input
                      type="text"
                      required
                      disabled={!!editingHost}
                      value={hostForm.host_tenant_id}
                      onChange={(e) => setHostForm({...hostForm, host_tenant_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder="unique-tenant-id"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Host Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={hostForm.host_name}
                      onChange={(e) => setHostForm({...hostForm, host_name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Host Name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={hostForm.description}
                    onChange={(e) => setHostForm({...hostForm, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Production database for CDR analytics"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMSC URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={hostForm.host_url}
                    onChange={(e) => setHostForm({...hostForm, host_url: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://smsc.example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The SMSC platform URL used for user authentication
                  </p>
                </div>
              </div>

              {/* Database Connections */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Database Connections</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PostgreSQL URI *
                  </label>
                  <input
                    type="text"
                    required
                    value={hostForm.postgres_uri}
                    onChange={(e) => handlePostgresUriChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="postgresql://user:password@host:5432/globalprocon"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Format: <code>postgresql://user:password@host:5432/dbname</code>.
                    Port defaults to 5432, database to <code>globalprocon</code>.
                    {editingHost && ' Omit the password from the URI to keep the stored one.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MongoDB URI *
                  </label>
                  <input
                    type="text"
                    required
                    value={hostForm.mongo_uri}
                    onChange={(e) => setHostForm({...hostForm, mongo_uri: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="mongodb://user:password@host:27017/globalprocon"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Format: <code>mongodb://user:password@host:27017/dbname</code>.
                    Port defaults to 27017, database to <code>globalprocon</code>.
                    {editingHost && ' Omit the password from the URI to keep the stored one.'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 hidden">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mongo Collection
                    </label>
                    <input
                      type="hidden"
                      value={hostForm.mongo_collection}
                      onChange={(e) => setHostForm({...hostForm, mongo_collection: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="MessageTx"
                    />
                  </div>

                  <div className="hidden">
                    <input
                      type="hidden"
                      value="t_message"
                      onChange={(e) => setHostForm({...hostForm, table_name: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Advanced Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SSL Mode
                    </label>
                    <select
                      value={hostForm.ssl_mode}
                      onChange={(e) => setHostForm({...hostForm, ssl_mode: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="disable">Disable</option>
                      <option value="prefer">Prefer</option>
                      <option value="require">Require</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Connection Timeout (s)
                    </label>
                    <input
                      type="number"
                      value={hostForm.connection_timeout}
                      onChange={(e) => setHostForm({...hostForm, connection_timeout: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sync Interval (s)
                    </label>
                    <input
                      type="number"
                      value={hostForm.sync_interval}
                      onChange={(e) => setHostForm({...hostForm, sync_interval: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3 pt-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hostForm.is_enable_etl}
                      onChange={(e) => setHostForm({...hostForm, is_enable_etl: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700">Enable ETL Sync</span>
                  </label>
                  <p className="text-xs text-gray-500">
                    When disabled, no data synchronization will occur for this host
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <button
                  type="button"
                  onClick={testConnection}
                  disabled={testingConnection}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  {testingConnection ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Database className="w-4 h-4" />
                  )}
                  <span>Test Connection</span>
                </button>

                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingHost(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingHost ? 'Update Host' : 'Create Host'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      </PageBody>
    </PageLayout>
  );
};

export default HostManagement;